import axios from 'axios';
import type { FormValues } from '../types';
import { templates } from '../data/templates';
import type { HistoryRecord, HistoryStatus } from '../../history/data/mockHistory';

const api = axios;

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['x-access-token'] = token;
  }
  return config;
});

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  id?: string | number;
  affectedRows?: number;
}

type BriefingPromocaoRow = Record<string, any>;

const LIST_FIELDS = new Set([
  'sellersIncluidos',
  'sellersExcecao',
  'cnpjsIncluidos',
  'cnpjsExcecao',
  'canais',
  'tipoPedido',
  'itensIncluidos',
  'itensExcluidos',
  'eansInclusao',
  'eansExclusao',
  'skusInclusao',
  'skusExclusao',
  'marcasInclusao',
  'marcasExclusao',
  'categoriasInclusao',
  'categoriasExclusao',
  'subcategoriasInclusao',
  'subcategoriasExclusao',
  'departamentosInclusao',
  'departamentosExclusao',
  'industriasInclusao',
  'industriasExclusao',
  'familiasInclusao',
  'familiasExclusao',
  'eansObrigatorios',
  'skusObrigatorios',
]);

const COLUMN_TO_FORM_KEY: Record<string, string> = {
  template_id: 'templateId',
  tipo_solicitacao: 'tipoSolicitacao',
  id_ifc: 'idIFC',
  tipo_mecanica: 'tipoMecanica',
  tipo_lista: 'tipoLista',
  recompra_disponivel: 'recompraDisponivel',
  id_lista_original: 'idListaOriginal',
  nome_promo: 'nomePromo',
  nome_exibicao: 'nomeExibicao',
  descricao: 'descricao',
  descricao_interna: 'descricaoInterna',
  tipo_desconto: 'tipoDesconto',
  valor_desconto: 'valorDesconto',
  industria: 'industria',
  multi_patrocinador: 'multiPatrocinador',
  patrocinadores: 'patrocinadores',
  cumulativa: 'cumulativa',
  inicio_vigencia: 'inicioVigencia',
  fim_vigencia: 'fimVigencia',
  budget_atrelado: 'budgetAtrelado',
  budget_valor: 'budgetValor',
  sellers_incluidos: 'sellersIncluidos',
  sellers_excecao: 'sellersExcecao',
  cnpjs_incluidos: 'cnpjsIncluidos',
  cnpjs_excecao: 'cnpjsExcecao',
  canais: 'canais',
  tipo_pedido: 'tipoPedido',
  criterio_desconto: 'criterioDesconto',
  qtd_minima: 'qtdMinima',
  qtd_maxima: 'qtdMaxima',
  valor_minimo: 'valorMinimo',
  valor_maximo: 'valorMaximo',
  numero_card: 'numeroCard',
  tipo_item_incluido: 'tipoItemIncluido',
  itens_incluidos: 'itensIncluidos',
  tipo_item_excluido: 'tipoItemExcluido',
  itens_excluidos: 'itensExcluidos',
  eans_inclusao: 'eansInclusao',
  eans_exclusao: 'eansExclusao',
  skus_inclusao: 'skusInclusao',
  skus_exclusao: 'skusExclusao',
  marcas_inclusao: 'marcasInclusao',
  marcas_exclusao: 'marcasExclusao',
  categorias_inclusao: 'categoriasInclusao',
  categorias_exclusao: 'categoriasExclusao',
  subcategorias_inclusao: 'subcategoriasInclusao',
  subcategorias_exclusao: 'subcategoriasExclusao',
  departamentos_inclusao: 'departamentosInclusao',
  departamentos_exclusao: 'departamentosExclusao',
  industrias_inclusao: 'industriasInclusao',
  industrias_exclusao: 'industriasExclusao',
  familias_inclusao: 'familiasInclusao',
  familias_exclusao: 'familiasExclusao',
  eans_obrigatorios: 'eansObrigatorios',
  skus_obrigatorios: 'skusObrigatorios',
  sku_combo: 'skuCombo',
  tipo_familia: 'tipoFamilia',
  nome_familia: 'nomeFamilia',
  industria_familia: 'industriaFamilia',
  obs_familia_existente: 'obsFamilia',
  skus_familia: 'skusFamilia',
  qtd_eans_distintos: 'qtdEANsDistintos',
  qtd_min_ativacao: 'qtdMinimaAtivacao',
  qtd_max_ativacao: 'qtdMaximaAtivacao',
  qtd_familias_ativacao: 'qtdFamiliasAtivacao',
  familia_obrigatoria: 'familiaObrigatoria',
  posicao_familia_lista: 'posicaoFamilia',
  desconto_familia: 'descontoFamilia',
  posicao_lista_secao: 'posicaoLista',
  secao_lista: 'secaoLista',
  cupom: 'cupom',
  codigo_cupom: 'codigoCupom',
  qtd_cupons_disponiveis: 'qtdCupons',
  qtd_usos_por_cliente: 'usosPorCliente',
};

function handleResponse<T>(res: { data: ApiResponse<T> }): T {
  if (!res.data.success) throw new Error(res.data.error || 'API Error');
  return res.data.data;
}

function handleMutationResponse<T = any>(res: { data: ApiResponse<T> }): ApiResponse<T> {
  if (!res.data.success) throw new Error(res.data.error || 'API Error');
  return res.data;
}

function splitList(value: any): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value === null || value === undefined || value === '') return [];

  const str = String(value).trim();
  if (!str) return [];

  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
  } catch (_) {
    // O backend atual grava listas separadas por vírgula.
  }

  return str
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatDateTimeToForm(value: any): string {
  if (!value) return '';

  if (typeof value === 'string') {
    const brMatch = value.match(/^\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}/);
    if (brMatch) return value.slice(0, 16);

    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (isoMatch) {
      const [, yyyy, mm, dd, hh, min] = isoMatch;
      return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formDateToIsoLike(value: any): string {
  if (!value) return '';

  if (typeof value === 'string') {
    const brMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/);
    if (brMatch) {
      const [, dd, mm, yyyy, hh, min] = brMatch;
      return `${yyyy}-${mm}-${dd}T${hh}:${min}:00`;
    }

    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (isoMatch) return value.replace(' ', 'T');
  }

  return String(value);
}

function normalizeScalar(value: any): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function getTemplateId(row: BriefingPromocaoRow, values: FormValues): string {
  const explicit = (values.templateId as string) || row.template_id;
  if (explicit && templates.some((template) => template.id === explicit)) return explicit;

  const tipoMecanica = String(values.tipoMecanica || row.tipo_mecanica || '');
  const tipoLista = String(values.tipoLista || row.tipo_lista || '');
  const cupom = String(values.cupom || row.cupom || '');

  if (tipoMecanica === '2') return 't2';
  if (tipoMecanica === '3') return 't3';
  if (tipoMecanica === '4') return tipoLista === '2' ? 't4b' : 't4a';
  if (tipoMecanica === '5') return 't5';
  if (tipoMecanica === '6') return 't6';
  if (tipoMecanica === '7') return 't7';
  if (cupom === '2') return 't8';

  return 't1';
}

function getStatus(values: FormValues): HistoryStatus {
  const startValue = formDateToIsoLike(values.inicioVigencia);
  const endValue = formDateToIsoLike(values.fimVigencia);

  if (!startValue || !endValue) return 'rascunho';

  const now = new Date();
  const start = new Date(startValue);
  const end = new Date(endValue);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'rascunho';
  if (now < start) return 'agendado';
  if (now > end) return 'encerrado';

  return 'ativo';
}

export function mapBriefingRowToHistoryRecord(row: BriefingPromocaoRow): HistoryRecord {
  const values: FormValues = {};

  Object.entries(COLUMN_TO_FORM_KEY).forEach(([column, key]) => {
    const raw = row[column];
    if (raw === undefined || raw === null) return;

    if (key === 'inicioVigencia' || key === 'fimVigencia') {
      values[key] = formatDateTimeToForm(raw);
    } else if (LIST_FIELDS.has(key)) {
      values[key] = splitList(raw);
    } else {
      values[key] = normalizeScalar(raw);
    }
  });

  const templateId = getTemplateId(row, values);
  values.templateId = templateId;

  const template = templates.find((item) => item.id === templateId) || templates[0];

  return {
    id: String(row.id),
    templateId,
    templateName: template?.name || 'Template não identificado',
    templateColor: template?.color || '#1976d2',
    templateNumber: template?.number || '—',
    createdAt: row.criado_em || row.created_at || '',
    updatedAt: row.atualizado_em || row.updated_at || row.criado_em || '',
    status: getStatus(values),
    author: row.criado_por_nome || row.atualizado_por_nome || '—',
    values,
  };
}

export async function fetchBriefingPromocoes(params?: Record<string, string | number | undefined>): Promise<HistoryRecord[]> {
  const res = await api.get('/briefing-promocoes', { params });
  const rows = handleResponse<BriefingPromocaoRow[]>(res);
  return rows.map(mapBriefingRowToHistoryRecord);
}

export async function fetchBriefingPromocao(id: string | number): Promise<HistoryRecord | null> {
  const res = await api.get(`/briefing-promocoes/${id}`);
  const row = handleResponse<BriefingPromocaoRow | null>(res);
  return row ? mapBriefingRowToHistoryRecord(row) : null;
}

export async function createBriefingPromocao(templateId: string, values: FormValues): Promise<string | number | undefined> {
  const res = await api.post('/briefing-promocoes', {
    ...values,
    templateId,
  });
  return handleMutationResponse(res).id;
}

export async function updateBriefingPromocao(id: string | number, templateId: string, values: FormValues): Promise<boolean> {
  const res = await api.put(`/briefing-promocoes/${id}`, {
    ...values,
    templateId,
  });
  handleMutationResponse(res);
  return true;
}

export async function deleteBriefingPromocao(id: string | number): Promise<boolean> {
  const res = await api.delete(`/briefing-promocoes/${id}`);
  handleMutationResponse(res);
  return true;
}


export interface BriefingPromocoesAgentResponse {
  message: string;
  formValues: FormValues;
  fields: FormValues;
  templateId: string | null;
  missingFields: string[];
  shouldFill: boolean;
}

export async function sendBriefingPromocoesAgentMessage(params: {
  chatId: string;
  message: string;
  templateId: string;
  templateName?: string;
  currentValues: FormValues;
}): Promise<BriefingPromocoesAgentResponse> {
  const res = await api.post<ApiResponse<BriefingPromocoesAgentResponse>>(
    '/briefing-promocoes-agent/chat',
    params
  );

  if (!res.data.success) {
    throw new Error(res.data.error || 'Erro ao conversar com o agente.');
  }

  return res.data.data;
}